import { queryOptions } from '@tanstack/react-query';

import { fieldsApi, GetPkResultsArgs } from '../apis/fields';
import { GetMapDto, GetMapsSummaryDto } from '../types/dto';
import type { MapPointScope } from '../utils/map-point-scope';

import { fieldMapsQueries } from './field-maps';

import { Filters, Globals, useQueriesWithGlobal } from '@data-access';

export const fieldsQueries = (global: Globals) => ({
  all: () => [global, 'fields'],

  maxBounds: (scope: MapPointScope = 'personal') =>
    queryOptions({
      queryKey: [...fieldsQueries(global).all(), 'maxBounds', scope],
      queryFn: () => fieldsApi.getMaxBounds(scope),
    }),
  lists: () => [...fieldsQueries(global).all(), 'list'],
  list: (filters: Filters) =>
    queryOptions({
      queryKey: [...fieldsQueries(global).lists(), filters],
      queryFn: () => fieldsApi.getAll(filters),
    }),

  details: () => [...fieldsQueries(global).all(), 'detail'],
  detail: (id: number) =>
    queryOptions({
      queryKey: [...fieldsQueries(global).details(), id],
      queryFn: () => fieldsApi.getOne(id),
    }),
  ilotDetail: (id: number, scope: MapPointScope = 'personal') =>
    queryOptions({
      queryKey: [...fieldsQueries(global).all(), 'ilot', id, scope],
      queryFn: () => fieldsApi.getIlot(id, scope),
    }),
  ilotInfos: (id: number) =>
    queryOptions({
      queryKey: [...fieldsQueries(global).all(), 'ilot', id, 'infos'],
      queryFn: () => fieldsApi.getIlotInfos(id),
    }),
  heatmapComputationStatus: (id: number) =>
    queryOptions({
      queryKey: [
        ...fieldsQueries(global).details(),
        id,
        'heatmapComputationStatus',
      ],
      queryFn: () => fieldsApi.getHeatmapComputationStatus(id),
      refetchInterval: (query) => {
        // refetch while status !== 'FINISHED'
        return query.state.data?.status === 'FINISHED' ? false : 5000;
      },
    }),
  map: (args: GetMapDto) =>
    queryOptions({
      queryKey: [
        ...fieldsQueries(global).detail(args.fieldId).queryKey,
        'maps',
        args.type,
        args.id,
      ],
      queryFn: () => fieldsApi.getMap(args),
    }),

  bounded: () => [...fieldsQueries(global).all(), 'bounded'],
  boundedList: (filters: {
    bbox: string;
    cluster: boolean;
    zoom: number;
    scope?: MapPointScope;
  }) =>
    queryOptions({
      queryKey: [
        ...fieldsQueries(global).bounded(),
        filters.bbox,
        filters.scope ?? 'personal',
      ],
      queryFn: () =>
        fieldsApi.getInBounds(
          filters.bbox,
          filters.cluster,
          filters.zoom,
          filters.scope,
        ),
    }),
  boundedHeatmaps: (filters: {
    bbox: string;
    layer: string;
    date: string;
  }) =>
    queryOptions({
      queryKey: [
        ...fieldsQueries(global).bounded(),
        { bbox: filters.bbox, layer: filters.layer, date: filters.date },
        'heatmaps',
      ],
      queryFn: () => fieldsApi.getHeatmapsInBounds(filters),
    }),

  values: () =>
    queryOptions({
      queryKey: [...fieldsQueries(global).all(), 'values'],
      queryFn: () => fieldsApi.getValues(),
    }),

  ...fieldMapsQueries(global),

  // pk
  pk: (fieldId: number) => [
    ...fieldsQueries(global).detail(fieldId).queryKey,
    'pk',
  ],
  pkTuto: (fieldId: number) =>
    queryOptions({
      queryKey: [...fieldsQueries(global).pk(fieldId), 'tuto'],
      queryFn: () => fieldsApi.getPkTuto(fieldId),
    }),
  pkValues: (fieldId: number) =>
    queryOptions({
      queryKey: [...fieldsQueries(global).pk(fieldId), 'values'],
      queryFn: () => fieldsApi.getPkValues(fieldId),
    }),
  pkAdvice: (fieldId: number) =>
    queryOptions({
      queryKey: [...fieldsQueries(global).pk(fieldId), 'advice'],
      queryFn: () => fieldsApi.getPkAdvice(fieldId),
    }),
  pkResults: (args: GetPkResultsArgs) =>
    queryOptions({
      queryKey: [...fieldsQueries(global).pk(args.fieldId), 'results', args],
      queryFn: () => fieldsApi.getPkResults(args),
    }),
});

export const useFieldsQueries = () => useQueriesWithGlobal(fieldsQueries);
